import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface Zone { id: string; name: string; code: string; airport_id: string | null; }
interface ZonePricing { id: string; zone_id: string; service_type_id: string; price: number; max_passengers: number; extra_cost_per_pax: number; }
interface KmRate { id: string; service_type_id: string; rate_per_km: number; min_price: number; }
interface ServiceType { id: string; name: string; }
interface Airport { id: string; name: string; code: string; }

const AdminZonesPricing = () => {
  const { toast } = useToast();
  const [zones, setZones] = useState<Zone[]>([]);
  const [pricing, setPricing] = useState<ZonePricing[]>([]);
  const [kmRates, setKmRates] = useState<KmRate[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);

  // Zone dialog
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [zoneForm, setZoneForm] = useState({ name: '', code: '', airport_id: '' });

  // Pricing dialog
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState<ZonePricing | null>(null);
  const [pricingForm, setPricingForm] = useState({ zone_id: '', service_type_id: '', price: 0, max_passengers: 4, extra_cost_per_pax: 0 });

  // KM Rate dialog
  const [kmDialogOpen, setKmDialogOpen] = useState(false);
  const [editingKm, setEditingKm] = useState<KmRate | null>(null);
  const [kmForm, setKmForm] = useState({ service_type_id: '', rate_per_km: 0, min_price: 0 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [z, p, k, st, a] = await Promise.all([
      supabase.from('transfer_zones').select('*'),
      supabase.from('zone_pricing').select('*'),
      supabase.from('km_rates').select('*'),
      supabase.from('service_types').select('*').order('sort_order'),
      supabase.from('transfer_airports').select('*').order('sort_order'),
    ]);
    setZones((z.data || []) as Zone[]);
    setPricing((p.data || []) as ZonePricing[]);
    setKmRates((k.data || []) as KmRate[]);
    setServiceTypes((st.data || []) as ServiceType[]);
    setAirports((a.data || []) as Airport[]);
    setLoading(false);
  };

  const getSTName = (id: string) => serviceTypes.find(s => s.id === id)?.name || '-';
  const getZoneName = (id: string) => zones.find(z => z.id === id)?.name || '-';
  const getAirportCode = (id: string | null) => airports.find(a => a.id === id)?.code || '-';

  // Zone CRUD
  const saveZone = async () => {
    const payload: any = { name: zoneForm.name, code: zoneForm.code, airport_id: zoneForm.airport_id || null };
    try {
      if (editingZone) {
        const { error } = await supabase.from('transfer_zones').update(payload).eq('id', editingZone.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('transfer_zones').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: 'Saved' }); setZoneDialogOpen(false); fetchAll();
    } catch (err: any) { toast({ title: 'Error', description: err.message, variant: 'destructive' }); }
  };

  const deleteZone = async (id: string) => {
    const { error } = await supabase.from('transfer_zones').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  // Pricing CRUD
  const savePricing = async () => {
    const payload: any = { zone_id: pricingForm.zone_id, service_type_id: pricingForm.service_type_id, price: pricingForm.price, max_passengers: pricingForm.max_passengers, extra_cost_per_pax: pricingForm.extra_cost_per_pax };
    try {
      if (editingPricing) {
        const { error } = await supabase.from('zone_pricing').update(payload).eq('id', editingPricing.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('zone_pricing').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: 'Saved' }); setPricingDialogOpen(false); fetchAll();
    } catch (err: any) { toast({ title: 'Error', description: err.message, variant: 'destructive' }); }
  };

  const deletePricing = async (id: string) => {
    const { error } = await supabase.from('zone_pricing').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  // KM Rate CRUD
  const saveKm = async () => {
    const payload: any = { service_type_id: kmForm.service_type_id, rate_per_km: kmForm.rate_per_km, min_price: kmForm.min_price };
    try {
      if (editingKm) {
        const { error } = await supabase.from('km_rates').update(payload).eq('id', editingKm.id) as any;
        if (error) throw error;
      } else {
        const { error } = await supabase.from('km_rates').insert(payload) as any;
        if (error) throw error;
      }
      toast({ title: 'Saved' }); setKmDialogOpen(false); fetchAll();
    } catch (err: any) { toast({ title: 'Error', description: err.message, variant: 'destructive' }); }
  };

  const deleteKm = async (id: string) => {
    const { error } = await supabase.from('km_rates').delete().eq('id', id) as any;
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Deleted' }); fetchAll(); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Zones & Pricing</h1>
          <p className="text-muted-foreground">Manage transfer zones, zone pricing, and km rates</p>
        </div>

        <Tabs defaultValue="zones">
          <TabsList>
            <TabsTrigger value="zones">Zones ({zones.length})</TabsTrigger>
            <TabsTrigger value="pricing">Zone Pricing ({pricing.length})</TabsTrigger>
            <TabsTrigger value="km">KM Rates ({kmRates.length})</TabsTrigger>
          </TabsList>

          {/* ZONES TAB */}
          <TabsContent value="zones" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setEditingZone(null); setZoneForm({ name: '', code: '', airport_id: '' }); setZoneDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add Zone</Button>
            </div>
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Airport</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {zones.map(z => (
                    <TableRow key={z.id}>
                      <TableCell className="font-medium">{z.name}</TableCell>
                      <TableCell className="font-mono">{z.code}</TableCell>
                      <TableCell>{getAirportCode(z.airport_id)}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingZone(z); setZoneForm({ name: z.name, code: z.code, airport_id: z.airport_id || '' }); setZoneDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete zone {z.name}?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteZone(z.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* ZONE PRICING TAB */}
          <TabsContent value="pricing" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setEditingPricing(null); setPricingForm({ zone_id: '', service_type_id: '', price: 0, max_passengers: 4, extra_cost_per_pax: 0 }); setPricingDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add Pricing</Button>
            </div>
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Zone</TableHead><TableHead>Service</TableHead><TableHead>Price</TableHead><TableHead>Max Pax</TableHead><TableHead>Extra/Pax</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pricing.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{getZoneName(p.zone_id)}</TableCell>
                      <TableCell>{getSTName(p.service_type_id)}</TableCell>
                      <TableCell className="font-bold">${p.price}</TableCell>
                      <TableCell>{p.max_passengers}</TableCell>
                      <TableCell>${p.extra_cost_per_pax}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingPricing(p); setPricingForm({ zone_id: p.zone_id, service_type_id: p.service_type_id, price: p.price, max_passengers: p.max_passengers, extra_cost_per_pax: p.extra_cost_per_pax }); setPricingDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this pricing?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deletePricing(p.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* KM RATES TAB */}
          <TabsContent value="km" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setEditingKm(null); setKmForm({ service_type_id: '', rate_per_km: 0, min_price: 0 }); setKmDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add KM Rate</Button>
            </div>
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Service Type</TableHead><TableHead>Rate/KM</TableHead><TableHead>Min Price</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {kmRates.map(k => (
                    <TableRow key={k.id}>
                      <TableCell>{getSTName(k.service_type_id)}</TableCell>
                      <TableCell className="font-bold">${k.rate_per_km}/km</TableCell>
                      <TableCell>${k.min_price}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingKm(k); setKmForm({ service_type_id: k.service_type_id, rate_per_km: k.rate_per_km, min_price: k.min_price }); setKmDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this rate?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteKm(k.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        {/* Zone Dialog */}
        <Dialog open={zoneDialogOpen} onOpenChange={setZoneDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingZone ? 'Edit Zone' : 'Add Zone'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={zoneForm.name} onChange={e => setZoneForm({...zoneForm, name: e.target.value})} /></div>
              <div><Label>Code</Label><Input value={zoneForm.code} onChange={e => setZoneForm({...zoneForm, code: e.target.value.toUpperCase()})} /></div>
              <div>
                <Label>Airport</Label>
                <Select value={zoneForm.airport_id} onValueChange={v => setZoneForm({...zoneForm, airport_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select airport" /></SelectTrigger>
                  <SelectContent>{airports.map(a => <SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={saveZone} className="w-full">{editingZone ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pricing Dialog */}
        <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingPricing ? 'Edit Pricing' : 'Add Pricing'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Zone</Label>
                <Select value={pricingForm.zone_id} onValueChange={v => setPricingForm({...pricingForm, zone_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select zone" /></SelectTrigger>
                  <SelectContent>{zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Service Type</Label>
                <Select value={pricingForm.service_type_id} onValueChange={v => setPricingForm({...pricingForm, service_type_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Price ($)</Label><Input type="number" value={pricingForm.price} onChange={e => setPricingForm({...pricingForm, price: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Max Pax</Label><Input type="number" value={pricingForm.max_passengers} onChange={e => setPricingForm({...pricingForm, max_passengers: parseInt(e.target.value) || 1})} /></div>
                <div><Label>Extra/Pax ($)</Label><Input type="number" value={pricingForm.extra_cost_per_pax} onChange={e => setPricingForm({...pricingForm, extra_cost_per_pax: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <Button onClick={savePricing} className="w-full">{editingPricing ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* KM Rate Dialog */}
        <Dialog open={kmDialogOpen} onOpenChange={setKmDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingKm ? 'Edit KM Rate' : 'Add KM Rate'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Service Type</Label>
                <Select value={kmForm.service_type_id} onValueChange={v => setKmForm({...kmForm, service_type_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Rate per KM ($)</Label><Input type="number" step="0.01" value={kmForm.rate_per_km} onChange={e => setKmForm({...kmForm, rate_per_km: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Min Price ($)</Label><Input type="number" value={kmForm.min_price} onChange={e => setKmForm({...kmForm, min_price: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <Button onClick={saveKm} className="w-full">{editingKm ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminZonesPricing;
